import { FC, useRef } from 'react';
import kebabCase from 'lodash/kebabCase';
import { FEATURES } from '@belong/feature-toggles/constants';
import { useFeatureToggles } from '@belong/feature-toggles/hooks';
import { ICON_NAMES, IOrganismMenuItem } from '@belong/types';
import { removeTrailingSlash } from '@belong/url-utils';
import { getVisibleHeaderMenuItems } from '../menuUtils';
import { GLOBAL_HEADER_TEST_ID as TEST_ID } from '../testids';
import { ARIA_CURRENT } from '../variables';
import { GHMobileLink } from './GHMobileLink';
import * as styles from './styles';
import { LinkWrapper } from './styles';
import { IGlobalHeaderMobile } from './types';

interface IMobileMainMenu extends Omit<IGlobalHeaderMobile, 'pageAlert' | 'onExpanded'> {
  handleOffCanvasClose: () => void;
  handleShowLinkPanel: (menuItem: IOrganismMenuItem) => void;
  setLauncherButtonRef: (node: HTMLButtonElement | null) => void;
}

export const GlobalHeaderMobileMainMenu: FC<IMobileMainMenu> = ({
  isLoggedIn,
  hideMyAccountLinks,
  currentUrl,
  urls,
  headerMenuContent,
  handleOffCanvasClose,
  handleShowLinkPanel,
  setLauncherButtonRef
}) => {
  const itemsRef = useRef<Map<IOrganismMenuItem, HTMLButtonElement | null> | null>(null);
  const { isFeatureOn } = useFeatureToggles();

  function getMap() {
    // initialize the Map on first usage
    itemsRef.current ??= new Map();
    return itemsRef.current;
  }

  function getNode(menuItem: IOrganismMenuItem) {
    const map = getMap();
    const node = map.get(menuItem);
    setLauncherButtonRef(node ?? null);
    return node;
  }

  const visibleHeaderMenuItems = getVisibleHeaderMenuItems(
    isLoggedIn,
    isFeatureOn(FEATURES.AUTHENTICATED_SHOP),
    headerMenuContent?.headerMenuItems
  );

  return (
    <>
      {isLoggedIn && !hideMyAccountLinks && (
        <>
          {/* My services */}
          {headerMenuContent.myServicesLabel && (
            <GHMobileLink
              aria-current={currentUrl === urls.myServices ? ARIA_CURRENT : undefined}
              data-testid={TEST_ID.BUTTON_MY_SERVICES}
              href={urls.myServices}
              label={headerMenuContent.myServicesLabel}
              onClick={handleOffCanvasClose}
              iconName={ICON_NAMES.MyServices}
            />
          )}
          {/* My profile */}
          {headerMenuContent.myProfileLabel && (
            <GHMobileLink
              aria-current={currentUrl === urls.myProfile ? ARIA_CURRENT : undefined}
              data-testid={TEST_ID.BUTTON_MY_PROFILE}
              href={urls.myProfile}
              label={headerMenuContent.myProfileLabel}
              onClick={handleOffCanvasClose}
              iconName={ICON_NAMES.Profile}
            />
          )}
          <LinkWrapper>
            <styles.StyledHR />
          </LinkWrapper>
        </>
      )}
      {visibleHeaderMenuItems.map(menuItem => {
        const hasNavGroup = menuItem.navGroup1 || menuItem.navGroup2;

        return (
          <GHMobileLink
            key={menuItem.parentLabel}
            aria-current={currentUrl === removeTrailingSlash(menuItem?.url?.href) ? ARIA_CURRENT : undefined}
            data-testid={`${TEST_ID.OFFCANVAS_HEADERLINK_PREFIX}${kebabCase(menuItem.parentLabel).toLowerCase()}`}
            label={menuItem.parentLabel}
            onClick={
              menuItem.navGroup1 || menuItem.navGroup2
                ? () => {
                    const node = getNode(menuItem);
                    node?.blur();
                    handleShowLinkPanel(menuItem);
                  }
                : () => handleOffCanvasClose()
            }
            linkRef={node => {
              const map = getMap();
              map?.set(menuItem, node);
            }}
            href={menuItem.url ? menuItem.url.href : undefined}
            iconName={hasNavGroup ? ICON_NAMES.ChevronRight : undefined}
            iconRight={!!hasNavGroup}
          />
        );
      })}
    </>
  );
};

GlobalHeaderMobileMainMenu.displayName = 'GlobalHeaderMobileMainMenu';
